/**
 * Right-side drawer with the full detail of a rule — identity, conditions
 * (plain English), actions (plain English), citations, and a collapsible
 * raw-JSON section for power users.
 */
import * as React from 'react';
import { Pencil, X } from 'lucide-react';
import useAuthStore from '../../stores/authStore';
import { RulesAPI } from '../../lib/rulesApi';
import { RuleActions, RuleConditions } from '../../lib/plainEnglish';
import { cn, getErrorMessage } from '../../lib/utils';
import {
  RuleTypeBadge,
  SeverityBadge,
  StatusPill,
  TagChip,
} from './RuleBadges';
import RuleEditForm from './RuleEditForm';
import type { RuleDetail as RuleDetailType, RuleListItem } from '../../types/rules';

const EDIT_ROLES = ['editor', 'expert', 'super_admin'];

interface RuleDetailProps {
  rule: RuleListItem | null;
  onClose: () => void;
  /** Called after a successful edit so the parent can refresh its list. */
  onUpdated?: (next: RuleDetailType) => void;
}

const RuleDetail: React.FC<RuleDetailProps> = ({ rule, onClose, onUpdated }) => {
  const { roles } = useAuthStore();
  const canEdit = EDIT_ROLES.some((r) => roles.includes(r));

  const [detail, setDetail] = React.useState<RuleDetailType | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [rawOpen, setRawOpen] = React.useState(false);
  // PRP-004 — structured editor state.
  const [editing, setEditing] = React.useState(false);
  const [savedAt, setSavedAt] = React.useState<number | null>(null);
  // PRP-011 — "Show plain English" toggle and lazy explain-on-demand.
  const [showPlainEnglish, setShowPlainEnglish] = React.useState(true);
  const [explanation, setExplanation] = React.useState<string | null>(null);
  const [explaining, setExplaining] = React.useState(false);

  React.useEffect(() => {
    if (!rule) return;
    let cancelled = false;
    setDetail(null);
    setError(null);
    setRawOpen(false);
    setEditing(false);
    setSavedAt(null);
    setExplanation(rule.plain_english_render || null);
    setLoading(true);
    RulesAPI.get(rule.id)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch((err) => {
        if (!cancelled) setError(getErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [rule]);

  // Close on Escape for keyboard users — but only when not editing,
  // to avoid silently dropping unsaved form input.
  React.useEffect(() => {
    if (!rule) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !editing) onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [rule, onClose, editing]);

  if (!rule) return null;

  const handleSaved = (next: RuleDetailType) => {
    setDetail(next);
    setEditing(false);
    setSavedAt(Date.now());
    onUpdated?.(next);
  };

  return (
    <div
      className="fixed inset-0 z-40 flex justify-end bg-slate-900/40"
      role="dialog"
      aria-modal="true"
      aria-label={`Rule detail: ${rule.name}`}
      onClick={onClose}
    >
      <div
        className="flex h-full w-full max-w-2xl flex-col overflow-y-auto bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b bg-white p-5">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <SeverityBadge severity={detail?.severity ?? rule.severity} />
              <StatusPill status={detail?.status ?? rule.status} />
              <RuleTypeBadge ruleType={detail?.rule_type ?? rule.rule_type} />
            </div>
            <h2 className="text-lg font-semibold text-slate-900">
              {detail?.name ?? rule.name}
            </h2>
            {(detail?.description || rule.description) && (
              <p className="text-sm text-slate-600">
                {detail?.description ?? rule.description}
              </p>
            )}
          </div>
          <div className="flex items-center gap-1">
            {canEdit && detail && !editing && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="inline-flex items-center gap-1.5 rounded border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
              >
                <Pencil className="h-3.5 w-3.5" />
                Edit
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="rounded-full p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
              aria-label="Close rule detail"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </header>

        <div className="space-y-6 p-5">
          {savedAt && !editing && (
            <div className="rounded border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800">
              Rule updated to version {detail?.version}.
            </div>
          )}

          {editing && detail && (
            <RuleEditForm
              rule={detail}
              onCancel={() => setEditing(false)}
              onSaved={handleSaved}
            />
          )}

          {!editing && (
          <>
          {/* Identity */}
          <Section title="Identity">
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <Field label="Category">{rule.category}</Field>
              <Field label="Priority">{String(rule.priority)}</Field>
              {detail && <Field label="Version">{String(detail.version)}</Field>}
              {detail && detail.damage_types.length > 0 && (
                <Field label="Damage types">{detail.damage_types.join(', ')}</Field>
              )}
              {detail && detail.jurisdictions.length > 0 && (
                <Field label="Jurisdictions">{detail.jurisdictions.join(', ')}</Field>
              )}
              {rule.domains.length > 0 && (
                <Field label="Domains" full>
                  <div className="flex flex-wrap gap-1">
                    {rule.domains.map((d) => (
                      <TagChip key={d}>{d}</TagChip>
                    ))}
                  </div>
                </Field>
              )}
              {rule.tags.length > 0 && (
                <Field label="Tags" full>
                  <div className="flex flex-wrap gap-1">
                    {rule.tags.map((t) => (
                      <TagChip key={t}>{t}</TagChip>
                    ))}
                  </div>
                </Field>
              )}
            </dl>
          </Section>

          {/* PRP-011: Plain English explanation toggle */}
          <Section title="Plain English">
            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={showPlainEnglish}
                  onChange={(e) => setShowPlainEnglish(e.target.checked)}
                  className="h-4 w-4"
                />
                Show plain English
              </label>
              {showPlainEnglish && (
                <div className="rounded border bg-slate-50 p-3 text-sm text-slate-800">
                  {explanation ? (
                    <p>{explanation}</p>
                  ) : explaining ? (
                    <p className="text-slate-500">Generating explanation…</p>
                  ) : (
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-slate-500 italic">
                        No cached explanation yet.
                      </p>
                      <button
                        type="button"
                        onClick={async () => {
                          if (!rule) return;
                          setExplaining(true);
                          try {
                            const r = await RulesAPI.explain(rule.id);
                            setExplanation(r.plain_english);
                          } catch (err) {
                            setExplanation(
                              `(failed to generate: ${getErrorMessage(err)})`,
                            );
                          } finally {
                            setExplaining(false);
                          }
                        }}
                        className="rounded-md bg-primary px-2 py-1 text-xs font-medium text-primary-foreground hover:bg-primary/90"
                      >
                        Generate
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Section>

          {/* Conditions */}
          <Section title="Conditions">
            {loading && <Loading label="Loading conditions…" />}
            {error && <ErrorBlock message={error} />}
            {detail && <RuleConditions conditions={detail.conditions} />}
          </Section>

          {/* Actions */}
          <Section title="Actions">
            {loading && <Loading label="Loading actions…" />}
            {error && <ErrorBlock message={error} />}
            {detail && <RuleActions actions={detail.actions} />}
          </Section>

          {/* Citations */}
          {detail && detail.citations.length > 0 && (
            <Section title={`Citations (${detail.citations.length})`}>
              <ul className="space-y-2 text-sm">
                {detail.citations.map((c, i) => (
                  <li
                    key={`${c.source_type}-${c.reference}-${i}`}
                    className="rounded border border-slate-200 bg-slate-50 p-3"
                  >
                    <div className="flex flex-wrap items-center gap-2 text-xs uppercase tracking-wide text-slate-600">
                      <span className="font-semibold">{c.source_type}</span>
                      <span className="text-slate-400">·</span>
                      <span>{c.reference}</span>
                      {c.jurisdiction && (
                        <>
                          <span className="text-slate-400">·</span>
                          <span>{c.jurisdiction}</span>
                        </>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-slate-700">{c.summary}</p>
                  </li>
                ))}
              </ul>
            </Section>
          )}

          {/* Raw JSON */}
          {detail && (
            <Section title="Advanced">
              <button
                type="button"
                onClick={() => setRawOpen((v) => !v)}
                className="text-xs font-medium text-primary hover:underline"
              >
                {rawOpen ? 'Hide raw rule JSON' : 'Show raw rule JSON'}
              </button>
              {rawOpen && (
                <pre className="mt-2 max-h-96 overflow-auto rounded border bg-slate-900 p-3 text-[11px] text-slate-100">
                  {JSON.stringify(detail, null, 2)}
                </pre>
              )}
            </Section>
          )}
          </>
          )}
        </div>
      </div>
    </div>
  );
};

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <section>
    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      {title}
    </h3>
    {children}
  </section>
);

const Field: React.FC<{
  label: string;
  children: React.ReactNode;
  full?: boolean;
}> = ({ label, children, full }) => (
  <div className={cn(full && 'col-span-2')}>
    <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</dt>
    <dd className="text-sm text-slate-800">{children}</dd>
  </div>
);

const Loading: React.FC<{ label: string }> = ({ label }) => (
  <div className="text-sm text-muted-foreground">{label}</div>
);

const ErrorBlock: React.FC<{ message: string }> = ({ message }) => (
  <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">
    {message}
  </div>
);

export default RuleDetail;
